// Lazy PostHog loader. The library (~60 KB gz) must not touch LCP, so it is
// dynamic-imported on first idle; events fired before it lands queue locally
// and flush on init. Privacy shape (consent rework, 2026-08-02): persistence
// follows the visitor's cookie choice — memory-only (anonymous, no stored IDs)
// until they Accept the banner, `localStorage+cookie` after, so returning
// visitors keep one identity. No autocapture, no session replay either way.
// Traffic goes through the same-origin /ingest proxy (worker/posthog-proxy.js)
// so ad-blocker domain lists don't silently eat it. The token is PostHog's
// public write-only project key (the same one baked into the desktop app).

import { getConsent, storeConsent, type Consent } from './consent';

const TOKEN = 'phc_w2cyanLxNe7nxdM6Vv7eh9MJyFCnSgy5fhqXcwBniDy3';

const persistenceFor = (c: Consent | null): 'localStorage+cookie' | 'memory' =>
  c === 'granted' ? 'localStorage+cookie' : 'memory';

let client: typeof import('posthog-js').default | null = null;
let loading = false;
let queue: Array<[string, Record<string, unknown>]> | null = [];
let pendingIdentify: [string, Record<string, unknown>] | null = null;
let lastError = '';

/** Diagnostics only (dev tooling + smoke scripts): the loader's internal state. */
export function phDebugState() {
  return { hasClient: !!client, loading, queueLen: queue?.length ?? null, lastError };
}

// Dev-only: pin the REAL instance's state reader on window — HMR timestamps
// mean an evaluate-time import can silently read a pristine duplicate module.
if (import.meta.env.DEV) {
  (window as unknown as { __phDebug?: typeof phDebugState }).__phDebug = phDebugState;
}

export function loadPostHog() {
  if (loading) return;
  loading = true;
  const kick = () => {
    import('posthog-js')
      .then(({ default: posthog }) => {
        posthog.init(TOKEN, {
          api_host: '/ingest', // same-origin proxy → eu.i.posthog.com (ad blockers block the domain, not ours)
          ui_host: 'https://eu.posthog.com',
          persistence: persistenceFor(getConsent()),
          person_profiles: 'identified_only',
          autocapture: false,
          capture_pageview: false, // TrackManager fires one $pageview per route
          capture_pageleave: true,
          disable_session_recording: true,
        });
        client = posthog;
        if (pendingIdentify) {
          client.identify(pendingIdentify[0], pendingIdentify[1]);
          pendingIdentify = null;
        }
        for (const [event, props] of queue ?? []) client.capture(event, props);
        queue = null;
      })
      .catch((e) => {
        lastError = String(e).slice(0, 300);
        queue = null; // blocked / offline — analytics just goes quiet
      });
  };
  const schedule = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(kick, { timeout: 3000 });
    } else {
      window.setTimeout(kick, 1200);
    }
  };
  // Background tabs: Chrome never runs idle callbacks in hidden pages (and
  // throttles their timers), so a tab opened in the background and closed
  // before focus would send NOTHING. Defer to the first moment of visibility.
  if (document.hidden) {
    const onVisible = () => {
      if (!document.hidden) {
        document.removeEventListener('visibilitychange', onVisible);
        schedule();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
  } else {
    schedule();
  }
}

export function phCapture(event: string, props: Record<string, unknown>) {
  if (client) {
    try {
      client.capture(event, props);
    } catch {
      /* never let tracking break the page */
    }
  } else if (queue) {
    queue.push([event, props]);
  }
}

/** Attach person properties (e.g. license_key_hash) to this visitor. */
export function phSetPerson(props: Record<string, unknown>) {
  phCapture('$set', { $set: props });
}

/**
 * The banner's (and privacy page's) single entry point. Stores the choice and
 * flips the live client's persistence so Accept works immediately — no reload.
 * Withdrawing (granted → denied) also resets the stored identity so the old
 * cookie ID stops being used.
 */
export function phSetConsent(v: Consent) {
  const prev = getConsent();
  storeConsent(v);
  if (client) {
    try {
      client.set_config({ persistence: persistenceFor(v) });
      if (v === 'denied' && prev === 'granted') client.reset();
    } catch {
      /* never let consent plumbing break the page */
    }
  }
  // Withdrawal must actually remove what consent created: set_config stops
  // USING the stored ID but leaves the ph_* entries behind (and an unloading
  // accepted tab can write them back). Purge explicitly.
  if (v === 'denied') {
    try {
      for (const k of Object.keys(localStorage)) if (k.startsWith('ph_')) localStorage.removeItem(k);
    } catch { /* no storage */ }
    try {
      for (const c of document.cookie.split(';')) {
        const name = c.trim().split('=')[0];
        if (name.startsWith('ph_')) document.cookie = `${name}=; max-age=0; path=/`;
      }
    } catch { /* cookies blocked */ }
  }
}

/**
 * Name the visitor at the money moments (trial request, account sign-in,
 * purchase). Their cookie journey merges into one person keyed by email —
 * founder-approved 2026-08-02. Works with or without cookie consent (memory
 * persistence still identifies the current session; contract basis).
 */
export function phIdentify(email: string, props: Record<string, unknown> = {}) {
  const id = email.trim().toLowerCase();
  if (!id || !id.includes('@')) return;
  const set = { email: id, ...props };
  if (client) {
    try {
      client.identify(id, set);
    } catch {
      /* never let tracking break the page */
    }
  } else {
    // loader still in flight (or queue open): apply right after init
    pendingIdentify = [id, set];
  }
}

/**
 * The cross-surface join key (founder approved 2026-07-27): sha256 of the
 * license key, first 12 hex chars — EXACTLY the backend's hash
 * (backend/server.py license_activate_endpoint), so a website purchase and an
 * app activation carry the same `license_key_hash` and can be joined in
 * PostHog. Never the key itself. Returns null when WebCrypto is unavailable.
 */
export async function hashLicenseKey(key: string): Promise<string | null> {
  try {
    const data = new TextEncoder().encode(key.trim());
    const digest = await crypto.subtle.digest('SHA-256', data);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 12);
  } catch {
    return null;
  }
}
